import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';
import { MAILING_TOPICS, getMyMailing, saveMyMailing, subscribeMailing } from '../services/mailingService';
import MailingTopicsField from './MailingTopicsField';
import { mailingButtonStyle, mailingLinkButtonStyle } from './mailingStyles';

/**
 * Account page: the signed-in user's raffle / event / newsletter emails.
 * Not subscribed → an invitation to subscribe. Also turns the register-form
 * opt-in (stored in user_metadata at sign-up, before the email was confirmed)
 * into a subscription the first time the user is signed in — only if there's
 * no subscription row yet, so it never overrides a later opt-out.
 */
const MailingAccountCard = ({ user, accessToken }) => {
  const { t } = useTranslation('mailing');
  const locale = useLocale();
  const [loaded, setLoaded] = useState(false);
  const [subscriber, setSubscriber] = useState(null);
  const [topics, setTopics] = useState([...MAILING_TOPICS]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const optedInAtSignup = !!user?.user_metadata?.marketing_opt_in;
  const name = user?.user_metadata?.full_name;

  useEffect(() => {
    if (!accessToken || !user?.email) return undefined;
    let cancelled = false;
    (async () => {
      try {
        let { subscriber: sub } = await getMyMailing(accessToken);
        if (!sub && optedInAtSignup) {
          await subscribeMailing({ email: user.email, name, source: 'register', locale, onlyIfNew: true }, accessToken);
          ({ subscriber: sub } = await getMyMailing(accessToken));
        }
        if (cancelled) return;
        setSubscriber(sub);
        if (sub?.status === 'subscribed') setTopics(sub.topics);
      } catch {
        // Non-blocking: the card just stays hidden
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken, user?.email, optedInAtSignup, name, locale]);

  const save = async (nextTopics) => {
    setSaving(true);
    setMessage(null);
    try {
      const { subscriber: sub } = await saveMyMailing(accessToken, { topics: nextTopics, name, locale });
      setSubscriber(sub);
      if (sub?.status === 'subscribed') {
        setTopics(sub.topics);
        setMessage(t('account.saved'));
      } else {
        setTopics([...MAILING_TOPICS]);
      }
    } catch {
      setMessage(t('account.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  const subscribed = subscriber?.status === 'subscribed';

  return (
    <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
      <h2 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: '#333', fontFamily: "'Gambado Sans', sans-serif" }}>{t('account.heading')}</h2>
      {subscribed ? (
        <>
          <p style={{ color: '#555', fontSize: '0.95rem', marginBottom: '1rem' }}>{t('account.subscribedAs', { email: subscriber.email })}</p>
          <MailingTopicsField value={topics} onChange={setTopics} disabled={saving} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', marginTop: '1.25rem' }}>
            <button type="button" onClick={() => save(topics)} disabled={saving || !topics.length} style={mailingButtonStyle}>
              {saving ? t('account.saving') : t('account.save')}
            </button>
            <button type="button" onClick={() => save([])} disabled={saving} style={mailingLinkButtonStyle}>
              {t('account.unsubscribeAll')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p style={{ color: '#555', fontSize: '0.95rem', marginBottom: '1rem' }}>{t('account.invite')}</p>
          <button type="button" onClick={() => save([...MAILING_TOPICS])} disabled={saving} style={mailingButtonStyle}>
            {saving ? t('account.saving') : t('account.subscribe')}
          </button>
        </>
      )}
      {message && <p role="status" style={{ color: '#555', fontSize: '0.9rem', margin: '0.75rem 0 0' }}>{message}</p>}
    </div>
  );
};

export default MailingAccountCard;
